import { Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form, required, validate } from '@angular/forms/signals'
import { MatDatepicker } from '@angular/material/datepicker'

import { DateService } from '@lemon/core/service/date.service'
import { Observable, Subject, takeUntil } from 'rxjs'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import {
  SelvitettavienTilanneraporttiPyynto,
  SelvitettavienTilanneraporttiVastaus,
  YlisuorituksetJaEraantyneetPyynto,
  YlisuorituksetJaEraantyneetVastaus,
  YllapitoTilastonTyyppi,
  YllapitoTilastotPyynto,
  YllapitoTilastotVastaus,
} from 'app/_jaettu-lemonator/model/raportit'
import { GetSalesReportDataRequest, GetSalesReportDataResponse, SalesReportRow } from 'app/_jaettu-lemonator/model/tilastot'

import { LocalDate } from '@lemon/core/model/common'
import { LaskuService } from 'app/_angular/service/lasku/lasku.service'
import { FileSaverService } from 'app/_jaettu-angular/service/file-saver'
import { LaskuLataaReskontratilannePyynto, LaskuLataaReskontratilanneVastaus } from 'app/_jaettu-lemonator/model/lasku'
import jszip from 'jszip' // NOTE! THIS IS NEEDED AS JSZIP DOESN'T OFFER ESM BUILD
import * as xlsx from 'xlsx'

interface KuukausiLomake {
  month: Date | null
}

interface AikavaliLomake {
  alkaa: Date | null
  loppuu: Date | null
}

interface PaivamaaraLomake {
  date: Date | null
}

interface ReskontratilanneLomake {
  reskontratilannePaiva: Date | null
}

@Component({
  templateUrl: './reports.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ReportsComponent implements OnInit, OnDestroy {
  private ngUnsubscribe: Subject<void> = new Subject<void>()

  month: Date = new Date()
  lomake: FieldTree<KuukausiLomake>

  paattyneetSopimuksetLomake: FieldTree<AikavaliLomake>
  hinnanmuutoksetLomake: FieldTree<AikavaliLomake>
  sopimustyyppienMuutoksetLomake: FieldTree<AikavaliLomake>
  poytalaatikkoJaTaukosopimustenKestotLomake: FieldTree<PaivamaaraLomake>
  saamisetOsakkailtaJaOmaisiltaLomake: FieldTree<PaivamaaraLomake>
  laskujenTuotteetLomake: FieldTree<AikavaliLomake>
  reskontratilanneLomake: FieldTree<ReskontratilanneLomake>
  ylisuorituksetJaEraantyneetLomake: FieldTree<PaivamaaraLomake>

  private readonly _injector = inject(Injector)

  readonly commonError = signal<string | null>(null)
  readonly commonErrorSelvitettavat = signal<string | null>(null)
  readonly commonErrorPaattyneetSopimukset = signal<string | null>(null)
  readonly commonErrorHinnanmuutokset = signal<string | null>(null)
  readonly commonErrorSopimustyyppienMuutokset = signal<string | null>(null)
  readonly commonErrorPoytalaatikkoJaTaukosopimustenKestot = signal<string | null>(null)
  readonly commonErrorSaamisetOsakkailtaJaOmaisilta = signal<string | null>(null)
  readonly commonErrorAsiakkaatJaHinnat = signal<string | null>(null)
  readonly commonErrorAsiakkaatJaHinnatJaLiikevaihto = signal<string | null>(null)
  readonly commonErrorLaskujenTuotteet = signal<string | null>(null)
  readonly commonErrorYlisuorituksetJaEraantyneet = signal<string | null>(null)
  readonly tanaan = new Date()

  kirjanpitajaOnDevaajaTaiTukiTaiMuutObservable: Observable<boolean>

  constructor(
    private _errorHandler: ErrorHandler,
    private _dateService: DateService,
    private _firebaseLemonator: FirebaseLemonator,
    private _ladataanService: LadataanService,
    private _fileSaverService: FileSaverService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _laskuService: LaskuService,
  ) {
    this.month = this._dateService.lisaaKuukausia(new Date(), -1)
    this.kirjanpitajaOnDevaajaTaiTukiTaiMuutObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaTaiTukiTaiMuutObservable
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  ngOnInit() {
    const injector = this._injector
    const aikavali = (alkaa: Date | null, loppuu: Date | null) =>
      form(
        signal<AikavaliLomake>({ alkaa, loppuu }),
        polku => {
          required(polku.alkaa)
          required(polku.loppuu)
        },
        { injector },
      )
    const paivamaara = () =>
      form(
        signal<PaivamaaraLomake>({ date: null }),
        polku => {
          required(polku.date)
        },
        { injector },
      )

    const lastOfPrevMonth = this._dateService.kuukaudenViimeinen(this._dateService.lisaaKuukausia(new Date(), -1))
    this.reskontratilanneLomake = form(
      signal<ReskontratilanneLomake>({ reskontratilannePaiva: lastOfPrevMonth }),
      polku => {
        required(polku.reskontratilannePaiva)
      },
      { injector },
    )

    this.ylisuorituksetJaEraantyneetLomake = form(
      signal<PaivamaaraLomake>({ date: this.tanaan }),
      polku => {
        required(polku.date)
        validate(polku.date, ({ value }) => (this._onkoTulevaisuudessa(value()) ? { kind: 'tulevaisuudessa' } : null))
      },
      { injector },
    )

    this.lomake = form(
      signal<KuukausiLomake>({ month: this.month }),
      polku => {
        required(polku.month)
      },
      { injector },
    )
    arvonMuutokset(this.lomake.month, { injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value: Date) => {
        this.commonError.set(null)
        this.month = value
      })

    this.paattyneetSopimuksetLomake = aikavali(null, null)
    arvonMuutokset(this.paattyneetSopimuksetLomake, { injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.commonErrorPaattyneetSopimukset.set(null)
      })

    this.hinnanmuutoksetLomake = aikavali(null, null)
    arvonMuutokset(this.hinnanmuutoksetLomake, { injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.commonErrorHinnanmuutokset.set(null)
      })

    this.sopimustyyppienMuutoksetLomake = aikavali(null, null)
    arvonMuutokset(this.sopimustyyppienMuutoksetLomake, { injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.commonErrorSopimustyyppienMuutokset.set(null)
      })

    this.poytalaatikkoJaTaukosopimustenKestotLomake = paivamaara()
    arvonMuutokset(this.poytalaatikkoJaTaukosopimustenKestotLomake, { injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.commonErrorPoytalaatikkoJaTaukosopimustenKestot.set(null)
      })

    this.saamisetOsakkailtaJaOmaisiltaLomake = paivamaara()
    arvonMuutokset(this.saamisetOsakkailtaJaOmaisiltaLomake, { injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.commonErrorSaamisetOsakkailtaJaOmaisilta.set(null)
      })

    const viimeKuukausi = this._dateService.lisaaKuukausia(new Date(), -1)
    this.laskujenTuotteetLomake = aikavali(this._dateService.kuukaudenEnsimmainen(viimeKuukausi), this._dateService.kuukaudenViimeinen(viimeKuukausi))
    arvonMuutokset(this.laskujenTuotteetLomake, { injector })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.commonErrorLaskujenTuotteet.set(null)
      })
  }

  chosenMonthHandler(normalizedMonth: Date, datepicker: MatDatepicker<Date>) {
    asetaArvo(this.lomake.month, normalizedMonth)
    datepicker.close()
  }

  async lataaSelvitettavatRaportti() {
    this._ladataanService.aloitaLataaminen()

    const requestData: SelvitettavienTilanneraporttiPyynto = {}

    try {
      const resp = await this._firebaseLemonator.functionsCall<SelvitettavienTilanneraporttiPyynto, SelvitettavienTilanneraporttiVastaus>('raportitLataaSelvitettavatTiimeittainCsvt', requestData)

      if (!resp) {
        this.commonErrorSelvitettavat.set('Raportin lataus epäonnistui. Ole hyvä ja yritä uudelleen.')
        return
      }

      if (!resp.tiedostot || Object.keys(resp.tiedostot).length < 1) {
        this.commonErrorSelvitettavat.set('Raportille ei palautunut tietoja. Ole hyvä ja yritä uudelleen.')
        return
      }

      const zip = new jszip()
      for (const entry of Object.entries(resp.tiedostot)) {
        zip.file(entry[0], entry[1], { date: new Date() })
      }

      const content = await zip.generateAsync({ type: 'blob' })

      const filename = 'Selvitettävien tilanne ' + this._dateService.muotoilePaikallinenPaiva(this._dateService.currentLocalDate(), 'fi') + '.zip'
      this._fileSaverService.saveAs(content, filename)
    } catch (err) {
      this._errorHandler.handleError(err)
      this.commonErrorSelvitettavat.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async lataaMyyntiraportti() {
    if (!this.lomake().valid()) {
      return
    }

    this._ladataanService.aloitaLataaminen()

    const selectedMonth = this.lomake.month().value()
    const selectedLocalMonth = this._dateService.dateToLocalMonth(selectedMonth)
    const requestData: GetSalesReportDataRequest = {
      month: this._dateService.localMonthToNumber(selectedLocalMonth),
    }

    try {
      const resp = await this._firebaseLemonator.functionsCall<GetSalesReportDataRequest, GetSalesReportDataResponse>('getSalesReportData', requestData)

      if (!resp || resp.e) {
        this.commonError.set('Myyntiraportin lataus epäonnistui! Ole hyvä ja yritä uudelleen.')
        return
      }

      if (!resp.data?.length) {
        this.commonError.set('Kuukaudelle ei löydy tietoja.')
        return
      }

      const dataAsArrayOfArrays = this._createExcelDataAsArrayOfArrays(resp.data)
      /* generate worksheet */
      const ws: xlsx.WorkSheet = xlsx.utils.aoa_to_sheet(dataAsArrayOfArrays)

      /* generate workbook and add the worksheet */
      const wb: xlsx.WorkBook = xlsx.utils.book_new()
      xlsx.utils.book_append_sheet(wb, ws, 'Myynnit')

      /* save to file */
      await xlsx.writeFile(wb, 'Myyntiraportti_' + selectedLocalMonth.month + '/' + selectedLocalMonth.year + '.xlsx')
    } catch (err) {
      this._errorHandler.handleError(err)
      this.commonError.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async lataaReskontratilanneExcel() {
    try {
      this._ladataanService.aloitaLataaminen()

      const date: Date = this.reskontratilanneLomake.reskontratilannePaiva().value()
      const arvopaiva = this._dateService.dateToLocalDate(date)
      const alkaen: LocalDate = { year: this._dateService.lisaaKuukausiaPaikallinen(arvopaiva, -24).year, month: 1, day: 1 }
      const pyynto: LaskuLataaReskontratilannePyynto = {
        alkaen: alkaen,
        arvopaiva: arvopaiva,
        paattyen: arvopaiva,
        asiakasAvain: this._laskuService.LEMONTREE_ASIAKAS_AVAIN,
      }
      const res = await this._firebaseLemonator.functionsCall<LaskuLataaReskontratilannePyynto, LaskuLataaReskontratilanneVastaus>('laskuLataaAsiakkaanReskontratilanne', pyynto)

      if (res.e) {
        throw new Error(res.e)
      }

      this._fileSaverService.saveBase64As(res.base64File, res.filename, 'xlsx')
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async lataaAlkaneetSopimuksetCsv() {
    if (!this.paattyneetSopimuksetLomake().valid()) {
      return
    }

    const alku = this.paattyneetSopimuksetLomake().value().alkaa
    const loppu = this._dateService.kuukaudenViimeinen(this.paattyneetSopimuksetLomake().value().loppuu)
    const requestData: YllapitoTilastotPyynto = {
      alku: this._dateService.dateToNumber(alku),
      loppu: this._dateService.dateToNumber(loppu),
      tyyppi: YllapitoTilastonTyyppi.ALOITTAVAT,
    }

    this.commonErrorPaattyneetSopimukset.set(null)
    this._ladataanService.aloitaLataaminen()

    try {
      const resp = await this._firebaseLemonator.functionsCall<YllapitoTilastotPyynto, YllapitoTilastotVastaus>('raportitTilastoCsv', requestData)

      if (!resp || resp.e) {
        this.commonErrorPaattyneetSopimukset.set('Raportin lataus epäonnistui. Ole hyvä ja yritä uudelleen.')
        return
      }

      if (!resp.csv) {
        this.commonErrorPaattyneetSopimukset.set('Raportille ei palautunut tietoja. Ole hyvä ja yritä uudelleen.')
        return
      }

      this._fileSaverService.saveStringAs(resp.csv, resp.filename, 'csv')
    } catch (err) {
      this._errorHandler.handleError(err)
      this.commonErrorPaattyneetSopimukset.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async lataaPaattyneetSopimuksetCsv() {
    if (!this.paattyneetSopimuksetLomake().valid()) {
      return
    }

    // Lopettavat: kerätään valitut kuukaudet - 1kk
    // Eli jos esim. valitaan tammi-helmikuu, kerätään sopimukset, jotka loppuneet joulu-tammikuu
    const alku = this._dateService.lisaaKuukausia(this.paattyneetSopimuksetLomake().value().alkaa, -1)
    const loppu = this._dateService.kuukaudenViimeinen(this._dateService.lisaaKuukausia(this.paattyneetSopimuksetLomake().value().loppuu, -1))
    const requestData: YllapitoTilastotPyynto = {
      alku: this._dateService.dateToNumber(alku),
      loppu: this._dateService.dateToNumber(loppu),
      tyyppi: YllapitoTilastonTyyppi.LOPETTAVAT,
    }

    this.commonErrorPaattyneetSopimukset.set(null)
    this._ladataanService.aloitaLataaminen()

    try {
      const resp = await this._firebaseLemonator.functionsCall<YllapitoTilastotPyynto, YllapitoTilastotVastaus>('raportitTilastoCsv', requestData)

      if (!resp || resp.e) {
        this.commonErrorPaattyneetSopimukset.set('Raportin lataus epäonnistui. Ole hyvä ja yritä uudelleen.')
        return
      }

      if (!resp.csv) {
        this.commonErrorPaattyneetSopimukset.set('Raportille ei palautunut tietoja. Ole hyvä ja yritä uudelleen.')
        return
      }

      this._fileSaverService.saveStringAs(resp.csv, resp.filename, 'csv')
    } catch (err) {
      this._errorHandler.handleError(err)
      this.commonErrorPaattyneetSopimukset.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async lataaHinnanmuutoksetCsv() {
    if (!this.hinnanmuutoksetLomake().valid()) {
      return
    }

    const alku = this._dateService.dateToNumber(this.hinnanmuutoksetLomake().value().alkaa)
    const loppu = this._dateService.dateToNumber(this._dateService.kuukaudenViimeinen(this.hinnanmuutoksetLomake().value().loppuu))
    const requestData: YllapitoTilastotPyynto = { alku, loppu, tyyppi: YllapitoTilastonTyyppi.HINNANMUUTOKSET }

    this.commonErrorHinnanmuutokset.set(null)
    this._ladataanService.aloitaLataaminen()

    try {
      const resp = await this._firebaseLemonator.functionsCall<YllapitoTilastotPyynto, YllapitoTilastotVastaus>('raportitTilastoCsv', requestData)

      if (!resp || resp.e) {
        this.commonErrorHinnanmuutokset.set('Raportin lataus epäonnistui. Ole hyvä ja yritä uudelleen.')
        return
      }

      if (!resp.csv) {
        this.commonErrorHinnanmuutokset.set('Raportille ei palautunut tietoja. Ole hyvä ja yritä uudelleen.')
        return
      }

      this._fileSaverService.saveStringAs(resp.csv, resp.filename, 'csv')
    } catch (err) {
      this._errorHandler.handleError(err)
      this.commonErrorHinnanmuutokset.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async lataaSopimustyyppienMuutoksetCsv() {
    if (!this.sopimustyyppienMuutoksetLomake().valid()) {
      return
    }

    const alku = this._dateService.dateToNumber(this.sopimustyyppienMuutoksetLomake().value().alkaa)
    const loppu = this._dateService.dateToNumber(this._dateService.kuukaudenViimeinen(this.sopimustyyppienMuutoksetLomake().value().loppuu))
    const requestData: YllapitoTilastotPyynto = { alku, loppu, tyyppi: YllapitoTilastonTyyppi.SOPIMUSTYYPPIEN_MUUTOKSET }

    this.commonErrorSopimustyyppienMuutokset.set(null)
    this._ladataanService.aloitaLataaminen()

    try {
      const resp = await this._firebaseLemonator.functionsCall<YllapitoTilastotPyynto, YllapitoTilastotVastaus>('raportitTilastoCsv', requestData)

      if (!resp || resp.e) {
        this.commonErrorSopimustyyppienMuutokset.set('Raportin lataus epäonnistui. Ole hyvä ja yritä uudelleen.')
        return
      }

      if (!resp.csv) {
        this.commonErrorSopimustyyppienMuutokset.set('Raportille ei palautunut tietoja. Ole hyvä ja yritä uudelleen.')
        return
      }

      this._fileSaverService.saveStringAs(resp.csv, resp.filename, 'csv')
    } catch (err) {
      this._errorHandler.handleError(err)
      this.commonErrorSopimustyyppienMuutokset.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async lataaPoytalaatikkoJaTaukosopimustenKestotCsv() {
    if (!this.poytalaatikkoJaTaukosopimustenKestotLomake().valid()) {
      return
    }

    const date = this._dateService.dateToNumber(this.poytalaatikkoJaTaukosopimustenKestotLomake().value().date)
    const requestData: YllapitoTilastotPyynto = { alku: date, loppu: null, tyyppi: YllapitoTilastonTyyppi.POYTALAATIKKO_JA_TAUKOSOPIMUSTEN_KESTOT }

    this.commonErrorPoytalaatikkoJaTaukosopimustenKestot.set(null)
    this._ladataanService.aloitaLataaminen()

    try {
      const resp = await this._firebaseLemonator.functionsCall<YllapitoTilastotPyynto, YllapitoTilastotVastaus>('raportitTilastoCsv', requestData)

      if (!resp || resp.e) {
        this.commonErrorPoytalaatikkoJaTaukosopimustenKestot.set('Raportin lataus epäonnistui. Ole hyvä ja yritä uudelleen.')
        return
      }

      if (!resp.csv) {
        this.commonErrorPoytalaatikkoJaTaukosopimustenKestot.set('Raportille ei palautunut tietoja. Ole hyvä ja yritä uudelleen.')
        return
      }

      this._fileSaverService.saveStringAs(resp.csv, resp.filename, 'csv')
    } catch (err) {
      this._errorHandler.handleError(err)
      this.commonErrorPoytalaatikkoJaTaukosopimustenKestot.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async lataaAsiakkaatJaHinnatCsv() {
    const requestData: YllapitoTilastotPyynto = { alku: null, loppu: null, tyyppi: YllapitoTilastonTyyppi.ASIAKKAAT_JA_HINNAT }

    this.commonErrorAsiakkaatJaHinnat.set(null)
    this._ladataanService.aloitaLataaminen()

    try {
      const resp = await this._firebaseLemonator.functionsCall<YllapitoTilastotPyynto, YllapitoTilastotVastaus>('raportitTilastoCsv', requestData)

      if (!resp || resp.e) {
        this.commonErrorAsiakkaatJaHinnat.set('Raportin lataus epäonnistui. Ole hyvä ja yritä uudelleen.')
        return
      }

      if (!resp.csv) {
        this.commonErrorAsiakkaatJaHinnat.set('Raportille ei palautunut tietoja. Ole hyvä ja yritä uudelleen.')
        return
      }

      this._fileSaverService.saveStringAs(resp.csv, resp.filename, 'csv')
    } catch (err) {
      this._errorHandler.handleError(err)
      this.commonErrorAsiakkaatJaHinnat.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  naytaTulevaisuusvirhe() {
    return this.ylisuorituksetJaEraantyneetLomake.date().errors().some(virhe => virhe.kind === 'tulevaisuudessa')
  }

  private _onkoTulevaisuudessa(paiva: Date | null) {
    return !!paiva && this._dateService.compareLocalDates(this._dateService.dateToLocalDate(paiva), '>', this._dateService.currentLocalDate())
  }

  async lataaYlisuorituksetJaEraantyneetCsv() {
    if (!this.ylisuorituksetJaEraantyneetLomake().valid()) {
      return
    }

    const requestData: YlisuorituksetJaEraantyneetPyynto = { paiva: this._dateService.dateToLocalDate(this.ylisuorituksetJaEraantyneetLomake().value().date) }

    this.commonErrorYlisuorituksetJaEraantyneet.set(null)
    this._ladataanService.aloitaLataaminen()

    try {
      const resp = await this._firebaseLemonator.functionsCall<YlisuorituksetJaEraantyneetPyynto, YlisuorituksetJaEraantyneetVastaus>('raportitLataaYlisuorituksetJaEraantyneetCsv', requestData)

      if (!resp || resp.e || !resp.csv) {
        this.commonErrorYlisuorituksetJaEraantyneet.set('Raportin lataus epäonnistui. Ole hyvä ja yritä uudelleen.')
        return
      }

      this._fileSaverService.saveStringAs(resp.csv, resp.filename, 'csv')
    } catch (err) {
      this._errorHandler.handleError(err)
      this.commonErrorYlisuorituksetJaEraantyneet.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async lataaAsiakkaatJaHinnatJaLiikevaihtoCsv() {
    const requestData: YllapitoTilastotPyynto = { alku: null, loppu: null, tyyppi: YllapitoTilastonTyyppi.ASIAKKAAT_JA_HINNAT_JA_LIIKEVAIHTO }

    this.commonErrorAsiakkaatJaHinnatJaLiikevaihto.set(null)
    this._ladataanService.aloitaLataaminen()

    try {
      const resp = await this._firebaseLemonator.functionsCall<YllapitoTilastotPyynto, YllapitoTilastotVastaus>('raportitTilastoCsv', requestData)

      if (!resp || resp.e) {
        this.commonErrorAsiakkaatJaHinnatJaLiikevaihto.set('Raportin lataus epäonnistui. Ole hyvä ja yritä uudelleen.')
        return
      }

      if (!resp.csv) {
        this.commonErrorAsiakkaatJaHinnatJaLiikevaihto.set('Raportille ei palautunut tietoja. Ole hyvä ja yritä uudelleen.')
        return
      }

      this._fileSaverService.saveStringAs(resp.csv, resp.filename, 'csv')
    } catch (err) {
      this._errorHandler.handleError(err)
      this.commonErrorAsiakkaatJaHinnatJaLiikevaihto.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async lataaLaskujenTuotteetCsv() {
    if (!this.laskujenTuotteetLomake().valid()) {
      return
    }

    const alku = this._dateService.dateToNumber(this.laskujenTuotteetLomake().value().alkaa)
    const loppu = this._dateService.dateToNumber(this.laskujenTuotteetLomake().value().loppuu)
    const requestData: YllapitoTilastotPyynto = { alku, loppu, tyyppi: YllapitoTilastonTyyppi.LASKUJEN_TUOTTEET }

    this.commonErrorLaskujenTuotteet.set(null)
    this._ladataanService.aloitaLataaminen()

    try {
      const resp = await this._firebaseLemonator.functionsCall<YllapitoTilastotPyynto, YllapitoTilastotVastaus>('raportitTilastoCsv', requestData)

      if (!resp || resp.e) {
        this.commonErrorLaskujenTuotteet.set('Raportin lataus epäonnistui. Ole hyvä ja yritä uudelleen.')
        return
      }

      if (!resp.csv) {
        this.commonErrorLaskujenTuotteet.set('Raportille ei palautunut tietoja. Ole hyvä ja yritä uudelleen.')
        return
      }

      this._fileSaverService.saveStringAs(resp.csv, resp.filename, 'csv')
    } catch (err) {
      this._errorHandler.handleError(err)
      this.commonErrorLaskujenTuotteet.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  async lataaSaamisetOsakkailtaJaOmaisiltaCsv() {
    if (!this.saamisetOsakkailtaJaOmaisiltaLomake().valid()) {
      return
    }

    const date = this._dateService.dateToNumber(this.saamisetOsakkailtaJaOmaisiltaLomake().value().date)
    const requestData: YllapitoTilastotPyynto = { alku: date, loppu: null, tyyppi: YllapitoTilastonTyyppi.SAAMISET_OSAKKAILTA_JA_OMAISILTA }

    this.commonErrorSaamisetOsakkailtaJaOmaisilta.set(null)
    this._ladataanService.aloitaLataaminen()

    try {
      const resp = await this._firebaseLemonator.functionsCall<YllapitoTilastotPyynto, YllapitoTilastotVastaus>('raportitTilastoCsv', requestData)

      if (!resp || resp.e) {
        this.commonErrorSaamisetOsakkailtaJaOmaisilta.set('Raportin lataus epäonnistui. Ole hyvä ja yritä uudelleen.')
        return
      }

      if (!resp.csv) {
        this.commonErrorSaamisetOsakkailtaJaOmaisilta.set('Raportille ei palautunut tietoja. Ole hyvä ja yritä uudelleen.')
        return
      }

      this._fileSaverService.saveStringAs(resp.csv, resp.filename, 'csv')
    } catch (err) {
      this._errorHandler.handleError(err)
      this.commonErrorSaamisetOsakkailtaJaOmaisilta.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  private _createPerSalesPersonExcelFormulas(data: SalesReportRow[]): string[][] {
    const salesPeople = Array.from(new Set(data.map(row => row.salesPerson)))
    const salesPeopleLength = salesPeople.length

    const output: string[][] = []

    const startRowIndex = salesPeopleLength + 3 // All the sales people rows + empty row + header row + jump to correct row
    const endRowIndex = startRowIndex + data.length

    for (const salesPerson of salesPeople) {
      const excelFormula = `=SUMIF(C${startRowIndex}:C${endRowIndex};"${salesPerson}"; B${startRowIndex}:B${endRowIndex})`

      output.push([salesPerson, excelFormula])
    }

    return output
  }

  private _createExcelDataAsArrayOfArrays(data: SalesReportRow[]): any[][] {
    const dataAsArrayOfArrays: any[][] = []

    const perPersonNumbersAoA = this._createPerSalesPersonExcelFormulas(data)

    for (const perPersonNumbers of perPersonNumbersAoA) {
      dataAsArrayOfArrays.push(perPersonNumbers)
    }

    /** Empty line in XLSX.
     *
     * Note! If this is removed or any new lines are added, also update the indexes in _createPerSalesPersonExcelFormulas
     **/
    dataAsArrayOfArrays.push([])

    xlsx.utils.aoa_to_sheet(perPersonNumbersAoA)

    dataAsArrayOfArrays.push(['Asiakkaan nimi', 'Hinta', 'Myyjän nimi'])

    for (const row of data || []) {
      dataAsArrayOfArrays.push([row.asiakkaanNimi, row.hinta, row.salesPerson])
    }

    return dataAsArrayOfArrays
  }
}
